import type { Observable } from 'rxjs'
import { BehaviorSubject } from 'rxjs'
import { filter, take } from 'rxjs/operators'

import { Query } from './query'
import type { VotingDTO } from './voting.dto'
import { Dispatcher } from './dispatcher'
import type { Repository } from './repository'
import { VoteObserver } from './vote.observer'
import { FirebaseClient, VotingRepository } from './firebase'

import { environment } from '~/config'

class Store {
  get ready() {
    return this._ready
  }

  protected _ready = false

  protected repository: Repository<VotingDTO> | undefined

  protected _ready$ = new BehaviorSubject(this.ready)

  readonly ready$: Observable<boolean> = this._ready$.asObservable()

  readonly dispatcher = new Dispatcher()

  constructor() {
    this.register()
  }

  async select() {
    await this.waitUntilIsReady()

    if (!this.repository) return []

    return this.repository.get(new Query())
  }

  protected async register() {
    if (this.ready || typeof window === 'undefined') return

    const firebaseClient = new FirebaseClient({
      apiKey: environment.FIREBASE_API_KEY!,
      authDomain: environment.FIREBASE_AUTH_DOMAIN!,
      databaseURL: environment.FIREBASE_DATABASE_URL!,
      projectId: environment.FIREBASE_PROJECT_ID!,
      storageBucket: environment.FIREBASE_STORAGE_BUCKET!,
      messagingSenderId: environment.FIREBASE_MESSAGING_SENDER_ID!,
      appId: environment.FIREBASE_APP_ID!,
      measurementId: environment.FIREBASE_MEASUREMENT_ID!,
      apiHost: environment.FIREBASE_API_HOST!,
    })

    await firebaseClient.connect()

    this.repository = new VotingRepository(firebaseClient)

    const observer = new VoteObserver(
      firebaseClient,
      this.repository as VotingRepository,
      this.dispatcher
    )

    setTimeout(() => observer.observe(), 600)

    this._ready$.next((this._ready = true))
  }

  protected waitUntilIsReady() {
    return new Promise(resolve => {
      if (this.ready) return resolve(true)

      this.ready$
        .pipe(
          filter(ready => ready),
          take(1)
        )
        .subscribe(() => resolve(true))
    })
  }
}

export const store = new Store()
